import { invokeCallHub, registerHandler } from "../realtime/callHubClient.js";

const lifecycleSubscriptions = new WeakMap();

export const getPayloadValue = (payload = {}, camelName, pascalName = "") =>
  payload?.[camelName] ?? payload?.[pascalName || `${camelName[0].toUpperCase()}${camelName.slice(1)}`];

export const getPayloadCallId = (payload = {}) =>
  getPayloadValue(payload, "callId") ||
  getPayloadValue(payload, "id") ||
  getPayloadValue(payload, "sourceId");

export const isSameCall = (payload = {}, callId) => {
  const payloadCallId = getPayloadCallId(payload);
  if (!payloadCallId || !callId) return true;
  return String(payloadCallId).toLowerCase() === String(callId).toLowerCase();
};

export const getPayloadMessage = (payload = {}, fallback = "Call service is not available.") =>
  getPayloadValue(payload, "message") || fallback;

export const createGenericCallInfo = (payload = {}, fallbackTitle = "Live call") => {
  const callId = getPayloadCallId(payload);
  const caller =
    getPayloadValue(payload, "callerDisplayName") ||
    getPayloadValue(payload, "callerName") ||
    getPayloadValue(payload, "displayName");
  const target =
    getPayloadValue(payload, "targetLabel") ||
    getPayloadValue(payload, "targetName");

  return {
    ...payload,
    callId,
    title: payload.title || payload.Title || caller || target || fallbackTitle,
    subtitle: payload.subtitle || payload.Subtitle || target || "",
  };
};

export const resolveHubConnection = (hubClient) => {
  if (!hubClient) return null;
  if (hubClient.connection) return hubClient.connection;
  if (typeof hubClient.getConnection === "function") return hubClient.getConnection();
  if (typeof hubClient.invoke === "function" || typeof hubClient.on === "function") return hubClient;
  return null;
};

export const startHubClient = async (hubClient) => {
  if (hubClient?.startConnection) return hubClient.startConnection();
  const connection = resolveHubConnection(hubClient);
  if (connection?.start) {
    await connection.start();
  }
  return connection;
};

export const invokeHubClient = (hubClient, connection, methodName, ...args) => {
  if (hubClient?.invoke) {
    return hubClient.invoke(methodName, ...args);
  }
  return invokeCallHub(connection, methodName, ...args);
};

export const registerHubHandler = (hubClient, connection, eventName, handler) => {
  if (hubClient?.registerHandler) {
    return hubClient.registerHandler(eventName, handler);
  }
  return registerHandler(connection, eventName, handler);
};

export const subscribeConnectionLifecycle = (connection, eventName, handler) => {
  if (!connection || !eventName || !handler) return () => {};

  const customSubscriber = connection[`subscribe${eventName[0].toUpperCase()}${eventName.slice(1)}`];
  if (typeof customSubscriber === "function") {
    return customSubscriber(handler);
  }

  let connectionSubscriptions = lifecycleSubscriptions.get(connection);
  if (!connectionSubscriptions) {
    connectionSubscriptions = new Map();
    lifecycleSubscriptions.set(connection, connectionSubscriptions);
  }

  let eventSubscription = connectionSubscriptions.get(eventName);
  if (!eventSubscription) {
    eventSubscription = new Set();
    connectionSubscriptions.set(eventName, eventSubscription);

    const signalRMethod = {
      reconnecting: "onreconnecting",
      reconnected: "onreconnected",
      close: "onclose",
    }[eventName];

    connection[signalRMethod]?.((...args) => {
      const currentSubscriptions = lifecycleSubscriptions.get(connection)?.get(eventName);
      currentSubscriptions?.forEach((subscriber) => subscriber(...args));
    });
  }

  eventSubscription.add(handler);
  return () => {
    eventSubscription.delete(handler);
  };
};
